<template>
  <div class="meeting-popup-root" v-if="meeting.active">
    <!-- Floating Icon (Minimized State) -->
    <transition name="fade-scale">
      <div
        v-if="meeting.minimized"
        class="floating-meeting-btn shadow-2"
        :style="floatingBtnStyle"
        @mousedown="startDrag"
        @touchstart.stop.prevent="startDrag"
        @click="handleBtnClick"
        title="Restore Meeting">
        <div class="pulse-ring"></div>
        <div class="btn-content">
          <i class="el-icon-video-camera"></i>
          <span class="live-badge">LIVE</span>
        </div>
      </div>
    </transition>

    <!-- Draggable Popup (Active State) -->
    <transition name="slide-up">
      <div v-show="!meeting.minimized" class="meeting-popup-container">
        <div class="popup-card shadow-lg">
          <div class="popup-header">
            <div class="header-left">
              <button class="header-btn minimize-btn" @click="minimizeMeeting" title="Minimize Meeting">
                <i class="el-icon-arrow-down"></i>
              </button>
              <div class="meeting-title">
                <span class="pulse-dot"></span>
                Live Meeting: {{ meeting.name }}
              </div>
            </div>
          </div>
          <div class="popup-body">
            <iframe
              v-if="meeting.url"
              :src="meeting.url"
              frameborder="0"
              class="meeting-iframe"
              allow="camera; microphone; display-capture; autoplay; clipboard-write"></iframe>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import {mapState, mapMutations} from 'vuex'

export default {
  name: 'MeetingPopup',
  data() {
    return {
      pos: {x: 0, y: 0},
      isDragging: false,
      dragStartX: 0,
      dragStartY: 0,
      initialX: 0,
      initialY: 0,
      hasMoved: false,
    }
  },
  computed: {
    ...mapState(['meeting']),
    floatingBtnStyle() {
      return {
        transform: `translate(${this.pos.x}px, ${this.pos.y}px)`,
        transition: this.isDragging ? 'none' : 'transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
      }
    },
  },
  methods: {
    ...mapMutations(['setMeeting']),
    minimizeMeeting() {
      this.setMeeting({minimized: true})
    },
    restoreMeeting() {
      this.setMeeting({minimized: false})
    },
    handleBtnClick() {
      if (!this.hasMoved) {
        this.restoreMeeting()
      }
    },
    startDrag(e) {
      this.isDragging = true
      this.hasMoved = false
      const event = e.touches ? e.touches[0] : e
      this.dragStartX = event.clientX
      this.dragStartY = event.clientY
      this.initialX = this.pos.x
      this.initialY = this.pos.y

      window.addEventListener('mousemove', this.onDrag)
      window.addEventListener('mouseup', this.stopDrag)
      window.addEventListener('touchmove', this.onDrag, {passive: false})
      window.addEventListener('touchend', this.stopDrag)
    },
    onDrag(e) {
      if (!this.isDragging) return
      const event = e.touches ? e.touches[0] : e
      const deltaX = event.clientX - this.dragStartX
      const deltaY = event.clientY - this.dragStartY

      if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
        this.hasMoved = true
      }

      this.pos.x = this.initialX + deltaX
      this.pos.y = this.initialY + deltaY

      // Prevent scrolling on touch
      if (e.touches) e.preventDefault()
    },
    stopDrag() {
      this.isDragging = false
      window.removeEventListener('mousemove', this.onDrag)
      window.removeEventListener('mouseup', this.stopDrag)
      window.removeEventListener('touchmove', this.onDrag)
      window.removeEventListener('touchend', this.stopDrag)
    },
  },
  mounted() {
    window.addEventListener('message', (event) => {
      if (event.data === 'livekit-leave') {
        this.setMeeting({active: false, minimized: false, url: ''})
      }
    })
  },
}
</script>

<style scoped lang="scss">
.meeting-popup-root {
  position: fixed;
  top: 0;
  left: 0;
  width: 0;
  height: 0;
  z-index: 1000000;
}

.floating-meeting-btn {
  position: fixed;
  bottom: 30px;
  right: 30px;
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  z-index: 1000001;
  user-select: none;
  touch-action: none;
  box-shadow: 0 10px 25px rgba(118, 75, 162, 0.4);

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 15px 30px rgba(118, 75, 162, 0.6);
  }

  .btn-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    i {
      font-size: 24px;
    }
    .live-badge {
      font-size: 9px;
      font-weight: bold;
      background: #ff4757;
      padding: 1px 4px;
      border-radius: 4px;
      margin-top: 2px;
    }
  }
}

.pulse-ring {
  position: absolute;
  width: 100%;
  height: 100%;
  background: rgba(118, 75, 162, 0.4);
  border-radius: 50%;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  100% {
    transform: scale(1.6);
    opacity: 0;
  }
}

.meeting-popup-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  pointer-events: none;
  z-index: 1000000;

  & > * {
    pointer-events: auto;
  }
}

.popup-card {
  width: 100vw;
  height: 100vh;
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  background: #1a1a1a;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.5);
}

.popup-header {
  height: 48px;
  background: rgba(255, 255, 255, 0.05);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  cursor: move;
  user-select: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);

  .header-left {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .meeting-title {
    color: #e0e0e0;
    font-size: 14px;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .pulse-dot {
    width: 8px;
    height: 8px;
    background: #ff4757;
    border-radius: 50%;
    box-shadow: 0 0 8px #ff4757;
    animation: flash 1s infinite alternate;
  }
}

@keyframes flash {
  from {
    opacity: 0.5;
  }
  to {
    opacity: 1;
  }
}

.header-btn {
  background: none;
  border: none;
  color: #a0a0a0;
  cursor: pointer;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    color: white;
    transform: translateY(1px);
  }

  &.minimize-btn:hover {
    color: #ffca28; // Amber color for minimize
  }

  &.close-btn:hover {
    background: #ff4757;
  }

  i {
    font-size: 16px;
  }
}

.popup-body {
  flex: 1;
  position: relative;
  background: #000;
}

.meeting-iframe {
  width: 100%;
  height: 100%;
  border: none;
  margin-top: 0px;
}

// Transitions
.fade-scale-enter-active,
.fade-scale-leave-active {
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.fade-scale-enter,
.fade-scale-leave-to {
  opacity: 0;
  transform: scale(0.5);
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}
.slide-up-enter,
.slide-up-leave-to {
  opacity: 0;
  transform: translateY(30px);
}
</style>
